import React, { useState, useEffect, useRef } from 'react';
import { LoadingOverlay, Box, Button } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { ImageData } from '../../types/Image';
import { EventData } from '../../types/Event';
import { predictEventForImages } from '../../api/Event';
import { EventDetailsForm } from '../shared/EventDetailsForm';

interface PredictEventDetailsFormWrapperProps {
    render: boolean;
    selectedImages: ImageData[];
    onConfirmEvent: (event: EventData) => void; // this is to be called with the final eventData object
}

/**
 * Wrapper around the shared EventDetailsForm component for the sortImages workflow
 * Handles prediction functionality and overlays
 */
export const PredictEventDetailsFormWrapper: React.FC<PredictEventDetailsFormWrapperProps> = ({
                                                                                                  render,
                                                                                                  selectedImages,
                                                                                                  onConfirmEvent
                                                                                              }) => {
    // Prediction state
    const [isPredicting, setIsPredicting] = useState(false);
    const [predictedData, setPredictedData] = useState<EventData | null>(null);

    const [eventData, setEventData] = useState<EventData | null>(null);

    // Styling state for AI-predicted fields (to visually indicate AI predictions)
    const [aiFields, setAiFields] = useState<{
        date: boolean;
        description: boolean;
        locationDescription: boolean;
        location: boolean;
    }>({
        date: false,
        description: false,
        locationDescription: false,
        location: false
    });

    // Only log on mount to avoid infinite loops
    useEffect(() => {
        console.log('[EventDetailsForm Wrapper] Initial render with:', {
            render,
            imageCount: selectedImages.length,
            eventData
        });
    }, []);

    // Only run prediction once when component first becomes visible
    // Use a ref to track if we've already initialized the event data
    const hasInitializedRef = useRef(false);

    useEffect(() => {
        // Only predict if we're rendering, have images, haven't initialized yet, and aren't already predicting
        if (render && selectedImages.length > 0 && !hasInitializedRef.current && !isPredicting) {
            console.log('[EventDetailsForm Wrapper] Initializing event data...');
            hasInitializedRef.current = true;
            predict();
        }
    }, [render, selectedImages, isPredicting]);

    // Track when prediction is complete
    useEffect(() => {
        console.log(`[EventDetailsForm Wrapper] Prediction status: ${isPredicting ? 'in progress' : 'complete'}`);
    }, [isPredicting]);

    // Log when component is not rendered
    useEffect(() => {
        console.log('[EventDetailsForm Wrapper] Component mounted with render =', render);
        return () => {
            console.log('[EventDetailsForm Wrapper] Component unmounted');
        };
    }, [render]);

    const predict = async () => {
        if (selectedImages.length === 0) {
            console.log('[EventDetailsForm Wrapper] Cannot predict: No images selected');
            return;
        }

        console.log(`[EventDetailsForm Wrapper] Starting prediction with ${selectedImages.length} images`);
        setIsPredicting(true);

        try {
            // First, create a basic event with just the images
            const baseEvent: EventData = {
                key: 'temp-event-' + Math.random().toString(36).substring(2, 11),
                images: selectedImages,
                title: '',
                keywords: [],
                attendees: []
            };

            // Set initial event data so the form is visible while we wait for predictions
            setEventData(baseEvent);

            // Call the AI prediction API
            console.log('[EventDetailsForm Wrapper] Calling predictEventForImages API...');
            const prediction: EventData = await predictEventForImages(selectedImages);
            console.log('[EventDetailsForm Wrapper] AI prediction received:', prediction);

            // Store the AI prediction to use as base data
            setPredictedData(prediction);
            setEventData(prediction);
            // todo: save to storage?

            // save to local storage also?
            const eventJson = JSON.stringify(prediction);
            localStorage.setItem('temp-event-data', eventJson);
            console.log('[EventDetailsForm] Updated event saved to localStorage');

            // Set AI field indicators
            const newAiFields = {
                date: !!prediction.date,
                description: !!prediction.title,
                locationDescription: !!prediction.location_description,
                location: !!prediction.location
            };
            setAiFields(newAiFields);

        } catch (error) {
            console.error('[EventDetailsForm Wrapper] Failed to predict event details:', error);

            // If prediction fails, still create a basic event so the user can fill in details manually
            if (!eventData) {
                const fallbackEvent: EventData = {
                    key: 'fallback-event-' + Math.random().toString(36).substring(2, 11),
                    images: selectedImages,
                    title: '',
                    keywords: [],
                    attendees: []
                };
                setEventData(fallbackEvent);
            }
        } finally {
            console.log('[EventDetailsForm Wrapper] Prediction complete, setting isPredicting to false');
            setIsPredicting(false);
        }
    };

    // Handle event updates from the shared component
    const handleEventUpdate = (partialUpdate: Partial<EventData>) => {
        console.log('[EventDetailsForm Wrapper] Event updated with partial data:', partialUpdate);

        // Reset AI highlight for changed fields
        const newAiFields = { ...aiFields };

        // Compare update fields with predicted values to determine which were manually edited
        if (predictedData) {
            // If date was updated
            if (partialUpdate.date) {
                const predictedDate = predictedData.date instanceof Date ?
                    predictedData.date :
                    (predictedData.date ? new Date(predictedData.date) : null);

                const updatedDate = partialUpdate.date instanceof Date ?
                    partialUpdate.date :
                    (partialUpdate.date ? new Date(partialUpdate.date) : null);

                if (predictedDate && updatedDate &&
                    predictedDate.getTime() !== updatedDate.getTime()) {
                    newAiFields.date = false;
                }
            }

            // If description was updated
            if (partialUpdate.title !== undefined && partialUpdate.title !== predictedData.title) {
                newAiFields.description = false;
            }

            // If location description was updated
            if (partialUpdate.location_description !== undefined &&
                partialUpdate.location_description !== predictedData.location_description) {
                newAiFields.locationDescription = false;
            }

            // If location was updated (simplified check)
            if (partialUpdate.location &&
                JSON.stringify(partialUpdate.location) !== JSON.stringify(predictedData.location)) {
                newAiFields.location = false;
            }

            setAiFields(newAiFields);
        }
        updateEventData(partialUpdate);
    };

    // Main function to update event data
    const updateEventData = (partialUpdate: Partial<EventData>) => {
        try {
            // Step 1: Get base event data (from state or localStorage)
            let baseEventData = getBaseEventData();

            // Step 2: Apply updates to the base data
            if (baseEventData) {
                applyUpdatesToEvent(baseEventData, partialUpdate);
            } else {
                // Step 3: Create new event if we couldn't get base data
                createNewEvent(partialUpdate);
            }
        } catch (error) {
            console.error('[EventDetailsForm] Error updating event data:', error);
            // Create a new event as last resort
            createNewEvent(partialUpdate);
        }
    };

// Helper function to get the base event data
    const getBaseEventData = () => {
        // Otherwise try to get from localStorage
        try {
            const savedEventData = localStorage.getItem('temp-event-data');
            if (savedEventData) {
                const parsedData = JSON.parse(savedEventData);

                // Verify this is for the current set of images
                if (parsedData && parsedData.images &&
                    selectedImages.length > 0 &&
                    parsedData.images[0]?.key === selectedImages[0]?.key) {
                    console.log('[EventDetailsForm] Loaded event data from localStorage', parsedData);
                    return parsedData;
                }
            }
        } catch (e) {
            console.error('[EventDetailsForm] Failed to load from localStorage:', e);
        }

        // If we get here, we couldn't find valid event data
        return null;
    };

// Helper function to apply updates and save them
    const applyUpdatesToEvent = (baseEventData: EventData, partialUpdate: Partial<EventData>) => {
        // Preserve keywords if not part of the update
        const keywords = partialUpdate.keywords || baseEventData.keywords || [];

        // Create updated event
        const updatedEvent = {
            ...baseEventData,
            ...partialUpdate,
            keywords: keywords
        };

        // Only update if something changed
        const baseJson = JSON.stringify(baseEventData);
        const updatedJson = JSON.stringify(updatedEvent);

        if (baseJson !== updatedJson) {
            // Save to localStorage and update state
            localStorage.setItem('temp-event-data', updatedJson);
            console.log('[EventDetailsForm] Updated event saved to localStorage');
            setEventData(updatedEvent);
        }
    };

// Helper function to create a new event
    const createNewEvent = (partialUpdate: Partial<EventData>) => {
        console.log('[EventDetailsForm] Creating new event data');

        if (selectedImages.length === 0) {
            console.error('[EventDetailsForm] Cannot create event: No images selected');
            return;
        }

        // Generate a stable key based on the first image's key
        const stableKey = selectedImages[0]?.key ?
            `event-for-${selectedImages[0].key.substring(0, 8)}` :
            'temp-event-' + Math.random().toString(36).substring(2, 8);

        const newEvent = {
            key: stableKey,
            images: selectedImages,
            title: partialUpdate.title || '',
            keywords: partialUpdate.keywords || [],
            attendees: [],
            ...partialUpdate
        };

        // Save to localStorage and update state
        localStorage.setItem('temp-event-data', JSON.stringify(newEvent));
        setEventData(newEvent);
    };

    if (!render) {
        console.log('[EventDetailsForm Wrapper] Not rendering due to render=false');
        return <></>;
    }

    // Removed console.log to prevent infinite re-rendering

    return (
        <Box style={{ position: 'relative' }}>
            {/* Prediction loading overlay */}
            <LoadingOverlay
                visible={isPredicting}
                loaderProps={{
                    children: 'Predicting Event Details...',
                    color: 'blue',
                    size: 'md'
                }}
                overlayProps={{
                    blur: 2,
                    opacity: 0.8
                }}
            />

            {/* AI Sparkles Indicators */}
            {Object.values(aiFields).some(Boolean) && !isPredicting && (
                <Box
                    style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        zIndex: 10,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: 8,
                        backgroundColor: 'rgba(224, 247, 250, 0.5)',
                        borderRadius: 4
                    }}
                >
                    <IconSparkles size={16} style={{ color: '#00BCD4' }} />
                    <span style={{ fontSize: 12, fontStyle: 'italic', color: '#00838F' }}>
                        AI-enhanced
                    </span>
                </Box>
            )}

            {/* Standard EventDetailsForm */}
            {eventData && (
                <EventDetailsForm
                    eventData={eventData}
                    onEventUpdate={handleEventUpdate}
                />
            )}
            {eventData && (
                <Button
                    variant="filled"
                    mt="xl"
                    fullWidth
                    onClick={() => {
                        if (eventData) {
                            console.log('[EventDetailsForm Wrapper] Confirming event:', eventData);
                            
                            // Clean up localStorage before confirming
                            try {
                                localStorage.removeItem('temp-event-data');
                                console.log('[EventDetailsForm Wrapper] Cleaned up localStorage');
                            } catch (e) {
                                console.error('[EventDetailsForm Wrapper] Error cleaning up localStorage:', e);
                            }
                            
                            onConfirmEvent(eventData);
                        }
                    }}
                    disabled={isPredicting}
                >
                    Confirm Event
                </Button>
            )}
        </Box>
    );
};